import React, { useState, useMemo } from "react";
import { useAuth } from "./common/Auth";
import { useCollection, useAllSystems, useServiceStatus } from "./context/AdminContext";
import { doc, deleteDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { firestore } from './common/firebase';
import { AI_HELP_SUBSCRIPTION_LEVELS, canUseAiHelp } from './aiHelp/entitlement';

const Admin = () => {
	const currentUser = useAuth();
	const { collection: users = [], loading: usersLoading } = useCollection('users', 100);
	const { systems = [], loading: systemsLoading } = useAllSystems(100);
	const { services, loading: servicesLoading } = useServiceStatus();

	const [selectedUserId, setSelectedUserId] = useState(null);

	const userMap = useMemo(() => {
		const map = {};
		users.forEach(u => { map[u.uid] = u; });
		return map;
	}, [users]);

	const selectedUser = selectedUserId ? userMap[selectedUserId] : null;

	const displayedSystems = selectedUserId 
		? systems.filter(s => s.userId === selectedUserId)
		: systems;

	const getOwnerDisplay = (system) => {
		const owner = userMap[system.userId];
		if (owner) {
			return `${owner.first || ''} ${owner.last || ''}`.trim() || owner.email || system.userId;
		}
		return system.userId || 'Unknown';
	};

	const handleSelectUser = (uid) => {
		setSelectedUserId(selectedUserId === uid ? null : uid);
	};

	const deleteUser = async (user) => {
		if (user.uid === currentUser?.uid) {
			alert("You cannot delete yourself.");
			return;
		}
		if (!window.confirm(`Delete user ${user.email || user.uid}? This will not remove their Firebase Auth account.`)) {
			return;
		}
		try {
			await deleteDoc(doc(firestore, 'users', user.uid));
			if (selectedUserId === user.uid) {
				setSelectedUserId(null);
			}
		} catch (e) {
			alert("Failed to delete user: " + e.message);
		}
	};

	const reassignSystem = async (system, newUserId) => {
		if (!newUserId || newUserId === system.userId) return;
		const newOwner = userMap[newUserId];
		const confirmMsg = `Reassign system "${system.name || system.uid}" from ${getOwnerDisplay(system)} to ${newOwner ? (newOwner.first + ' ' + newOwner.last).trim() || newOwner.email : newUserId}?`;
		if (!window.confirm(confirmMsg)) return;
		try {
			await updateDoc(doc(firestore, 'systems', system.uid), { userId: newUserId });
		} catch (e) {
			alert("Failed to reassign system: " + e.message);
		}
	};

	const setSubscriptionLevel = async (user, level) => {
		try {
			const payload = {
				subscription_level: level || '',
			};
			if (level) {
				payload.subscription_active = true;
			}
			await updateDoc(doc(firestore, 'users', user.uid), payload);
		} catch (e) {
			alert('Failed to update subscription level: ' + e.message);
		}
	};

	const toggleAiHelpFeature = async (user, enable) => {
		try {
			await updateDoc(doc(firestore, 'users', user.uid), {
				features: enable ? arrayUnion('ai_help') : arrayRemove('ai_help'),
			});
		} catch (e) {
			alert('Failed to update ai_help feature: ' + e.message);
		}
	};

	return (
		<div className="p-4 sm:p-6 max-w-6xl mx-auto">
			<div className="mb-6">
				<h1 className="text-2xl font-semibold tracking-tight">Admin Dashboard</h1>
				<p className="text-sm text-gray-500 mt-1">Manage users, delete accounts, and re-assign system ownership.</p>
			</div>

			<div className="mb-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
				<h3 className="font-semibold text-sm mb-3">Services</h3>
				{servicesLoading ? (
					<div className="text-sm text-gray-500">Checking services...</div>
				) : (
					<div className="flex flex-col sm:flex-row gap-3">
						{services.map((svc) => (
							<div
								key={svc.id}
								className="flex-1 flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-800"
							>
								<div className="min-w-0">
									<div className="text-sm font-medium truncate">{svc.label}</div>
									<div className="text-xs text-gray-500">Last seen: {svc.lastSeen}</div>
								</div>
								<div className="flex items-center gap-2 flex-shrink-0">
									<span
										className={`inline-block w-2 h-2 rounded-full ${svc.running ? 'bg-green-500' : 'bg-red-500'}`}
										aria-hidden
									/>
									<span className={`text-xs font-medium ${svc.running ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
										{svc.running ? 'Running' : 'Not running'}
									</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
				{/* Users Panel */}
				<div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
					<div className="flex items-center justify-between mb-3">
						<h3 className="font-semibold">Users ({users.length})</h3>
						{selectedUserId && (
							<button 
								onClick={() => setSelectedUserId(null)} 
								className="text-xs px-2 py-1 border rounded hover:bg-gray-100 dark:hover:bg-gray-800"
							>
								Clear Filter
							</button>
						)}
					</div>
					<div className="space-y-1 text-sm max-h-[420px] overflow-auto pr-1">
						{usersLoading ? (
							<div className="text-gray-500">Loading users...</div>
						) : users.length > 0 ? users.map(u => {
							const isSelected = selectedUserId === u.uid;
							return (
								<div 
									key={u.uid} 
									onClick={() => handleSelectUser(u.uid)}
									className={`flex justify-between items-center p-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-[#880e4f] text-white' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
								>
									<div className="min-w-0">
										<div className="font-medium truncate">{u.first} {u.last}</div>
										<div className={`text-xs truncate ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>{u.email}</div>
									</div>
									<div className="flex items-center gap-2 flex-shrink-0">
										<div className={`text-xs px-2 py-0.5 rounded ${isSelected ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
											{(u.roles || []).join(', ') || 'user'}
										</div>
										<button
											onClick={(e) => { e.stopPropagation(); deleteUser(u); }}
											className={`text-xs px-2 py-0.5 rounded border ${isSelected ? 'border-white/40 hover:bg-white/10' : 'border-red-200 hover:bg-red-50 dark:hover:bg-red-950 text-red-600'}`}
											title="Delete user"
										>
											Delete
										</button>
									</div>
								</div>
							);
						}) : <div className="text-gray-500">No users loaded.</div>}
					</div>
					<div className="mt-3 text-xs text-gray-400">Click a user to filter systems. Current user: {currentUser?.email}</div>
				</div>

				{/* Systems Panel */}
				<div className="lg:col-span-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
					<div className="flex items-center justify-between mb-3">
						<h3 className="font-semibold">
							Systems {selectedUser ? `for ${selectedUser.first} ${selectedUser.last}` : '(all)'} ({displayedSystems.length})
						</h3>
						{selectedUserId && (
							<button onClick={() => setSelectedUserId(null)} className="text-xs px-2 py-1 border rounded hover:bg-gray-100 dark:hover:bg-gray-800">
								Show All
							</button>
						)}
					</div>

					{selectedUser && (
						<div className="mb-4 p-3 rounded-xl border border-[#880e4f]/25 bg-[#880e4f]/5 space-y-2">
							<div className="text-sm font-medium text-[#880e4f]">Jules entitlement</div>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								Grant via <code className="text-[10px]">subscription_level</code> (pro / ai / enterprise + active)
								{' '}or feature tag <code className="text-[10px]">ai_help</code>.
								{' '}Status: {canUseAiHelp(selectedUser) ? 'entitled' : 'not entitled'}.
							</p>
							<div className="flex flex-wrap items-center gap-2 text-xs">
								<label className="flex items-center gap-1">
									<span className="text-gray-500">Level</span>
									<select
										className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-2 py-1"
										value={selectedUser.subscription_level || ''}
										onChange={(e) => setSubscriptionLevel(selectedUser, e.target.value)}
									>
										<option value="">(none)</option>
										{AI_HELP_SUBSCRIPTION_LEVELS.map((level) => (
											<option key={level} value={level}>{level}</option>
										))}
									</select>
								</label>
								<label className="flex items-center gap-1.5">
									<input
										type="checkbox"
										checked={(selectedUser.features || []).includes('ai_help')}
										onChange={(e) => toggleAiHelpFeature(selectedUser, e.target.checked)}
									/>
									<span>ai_help feature</span>
								</label>
								{(selectedUser.features || []).includes('ai_help') && (
									<span className="text-[10px] px-1.5 py-0.5 rounded bg-[#880e4f]/15 text-[#880e4f]">features: ai_help</span>
								)}
							</div>
						</div>
					)}

					<div className="space-y-2 text-sm max-h-[420px] overflow-auto pr-1">
						{systemsLoading ? (
							<div className="text-gray-500">Loading systems...</div>
						) : displayedSystems.length > 0 ? displayedSystems.map(s => (
							<div key={s.uid} className="p-3 bg-gray-50 dark:bg-gray-800 rounded flex flex-col sm:flex-row sm:items-center gap-3">
								<div className="flex-1 min-w-0">
									<div className="font-medium truncate">{s.name || s.uid}</div>
									<div className="text-xs text-gray-500 truncate">{s.location || 'No location'}</div>
									<div className="mt-1 text-[10px] text-gray-400">
										Owner: <span className="font-medium text-gray-600 dark:text-gray-300">{getOwnerDisplay(s)}</span>
									</div>
								</div>

								<div className="flex items-center gap-2 text-xs">
									<select 
										value={s.userId || ''}
										onChange={(e) => reassignSystem(s, e.target.value)}
										className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-xs"
									>
										{users.map(u => (
											<option key={u.uid} value={u.uid}>
												{u.first} {u.last} ({u.email})
											</option>
										))}
									</select>
									<span className="text-gray-400 hidden sm:inline">reassign</span>
								</div>
							</div>
						)) : <div className="text-gray-500">No systems{selectedUser ? ' for this user' : ''}.</div>}
					</div>

					<div className="mt-3 text-xs text-gray-400">
						Selecting a user filters the list. Use the dropdown next to a system to re-assign ownership.
					</div>
				</div>
			</div>

			<div className="mt-6 text-xs text-gray-400 space-y-1">
				<p>Note: Deleting a user only removes the Firestore document (Auth deletion requires admin privileges or Cloud Functions).</p>
				<p>
					Jules beta: set <code>users.subscription_level</code> to pro/ai/enterprise with <code>subscription_active: true</code>,
					or add <code>ai_help</code> to <code>users.features</code> (panel above when a user is selected). Stripe can later map plans to <code>subscription_level: &apos;ai&apos;</code>.
				</p>
			</div>
		</div>
	);
}

export default Admin;
